import { Column, Row } from 'app/design/primitives/box';
import { Input } from 'app/design/primitives/input';
import { Text } from 'app/design/primitives/text';

import { useScopedTranslation } from 'app/hooks/i18n';
import { useState } from 'react';
import { Keyboard, Pressable, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ModalTitle } from './components/modal-title';
import type { useBorrowerNextScreen } from './use-borrower-next-screen';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronRight, IconCircleX, IconSearch } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();
const CONTENT_CONTAINER_STYLE = {
  flexGrow: 1,
  paddingHorizontal: theme.spacing['spacing.general.x4'],
  paddingBottom: theme.spacing['spacing.general.x8'],
};

type BorrowerNextScreenState = ReturnType<typeof useBorrowerNextScreen>;

export function BorrowerNextScreenBody({ close, openInspiration, searchObject }: BorrowerNextScreenState) {
  const { t } = useScopedTranslation();
  const foreground = theme.colors['text.primary'];
  const [searchValue, setSearchValue] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const showSearchClear = isSearchFocused || searchValue.length > 0;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: theme.colors['surface.tertiary'] }}>
      <ScrollView
        contentContainerStyle={CONTENT_CONTAINER_STYLE}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <Column alignItems="center" justifyContent="space-between" marginBottom="spacing.general.x8" minHeight={44}>
          <ModalTitle close={close} />
        </Column>

        <Text fontWeight="700" marginBottom="spacing.general.x4" color="text.primary" style={{ fontSize: 36 }}>
          {t('title')}
        </Text>

        <Column gap="spacing.general.x2" marginBottom="spacing.general.x4">
          <Text fontSize={14} color="text.primary" fontWeight="bold">
            {t('searchLabel')}
          </Text>
          <Input
            backgroundColor={theme.colors.white}
            enablesReturnKeyAutomatically
            fieldBorderColor={isSearchFocused ? 'text.primary' : 'border.secondary'}
            fieldBorderRadius="radius.full"
            fieldBorderWidth={isSearchFocused ? 2 : 1}
            left={<IconSearch strokeWidth={ICON_STROKE_WIDTH} color={foreground} />}
            onBlur={() => {
              setIsSearchFocused(false);
              Keyboard.dismiss();
            }}
            onChangeText={setSearchValue}
            onFocus={() => setIsSearchFocused(true)}
            onSubmitEditing={() => searchObject(searchValue)}
            placeholder={t('searchPlaceholder')}
            returnKeyType="search"
            right={
              showSearchClear ? (
                <Pressable hitSlop={12} onPress={() => setSearchValue('')}>
                  <IconCircleX strokeWidth={ICON_STROKE_WIDTH} size={24} color={foreground} />
                </Pressable>
              ) : undefined
            }
            submitBehavior="blurAndSubmit"
            value={searchValue}
          />
        </Column>

        <Pressable onPress={openInspiration}>
          <Row
            alignItems="center"
            gap="spacing.general.x4"
            style={{
              backgroundColor: theme.colors.white,
              borderRadius: theme.borderRadii['radius.md'],
              paddingHorizontal: theme.spacing['spacing.general.x4'],
              paddingVertical: theme.spacing['spacing.general.x4'],
            }}
          >
            <Text fontSize={28}>🙋‍♀️</Text>
            <Text flex={1} fontSize={16} fontWeight="700" style={{ color: foreground }}>
              {t('inspirationButton')}
            </Text>
            <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={24} color={foreground} />
          </Row>
        </Pressable>
      </ScrollView>
    </SafeAreaView>
  );
}
